import React, { useState } from 'react';
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { collection, query, where, getDocs, doc, setDoc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { db, auth } from '../firebase';
import { UploadCloud, AlertTriangle, CheckCircle, FileText, X } from 'lucide-react';
import { Button } from './ui/Button';
import { useUserFacingError } from '../hooks/useUserFacingError';

export const UploadForm = ({ onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [farmName, setFarmName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const { formatError, logError } = useUserFacingError();

  // 1. Handle File Selection
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && (selected.name.endsWith('.kmz') || selected.name.endsWith('.kml'))) {
      setFile(selected);
      // Auto-fill name if empty
      if (!farmName) {
        setFarmName(selected.name.replace(/\.kmz|\.kml/gi, ''));
      }
      setError(null);
    } else {
      setFile(null);
      setError('Please select a valid .kmz or .kml file');
    }
  };

  // 2. The Registration & Upload Logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !farmName) return;
    if (!auth.currentUser) {
      setError("You must be logged in.");
      return;
    }

    setUploading(true);
    setError(null);
    setWarning(null);

    try {
      const user = auth.currentUser;
      const farmsRef = collection(db, "farms");

      // A. CHECK FOR DUPLICATES
      // (Simple check: Does this user already have a farm with this name?)
      const q = query(farmsRef, where("userId", "==", user.uid), where("farmName", "==", farmName));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setWarning("You already have a farm with this name. Please choose a unique name.");
        setUploading(false);
        return; 
      }

      // B. REGISTER FARM IN DATABASE
      // We create the document *before* uploading to reserve the ID
      const newFarmRef = doc(collection(db, "farms"));
      const farmId = newFarmRef.id; // This is our secure, unique ID

      await setDoc(newFarmRef, {
        farmId: farmId,             // Internal ID (used for asset matching)
        farmName: farmName,         // Display Name (e.g. "Green Valley")
        userId: user.uid,           // Ownership
        originalFileName: file.name,
        status: 'PROCESSING',       // Initial State
        uploadedAt: serverTimestamp(),
        mvpMode: true
      });

      // Add this farm to the user's farmIds so it appears in their dashboard
      try {
        await updateDoc(doc(db, 'users', user.uid), { farmIds: arrayUnion(farmId) });
      } catch (_) {
        // User doc may not exist yet; create it
        const seed = { farmIds: [farmId], role: 'user' };
        if (user.email) seed.email = user.email;
        await setDoc(doc(db, 'users', user.uid), seed, { merge: true });
      }

      // C. UPLOAD FILE (Renamed to match ID)
      // We rename 'MyFarm.kmz' -> 'abc123xyz.kmz'
      // This ensures the Python script updates the correct DB record.
      const storage = getStorage();
      const storageRef = ref(storage, `${farmId}.kmz`);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          // You could add a progress bar here if you wanted
        },
        (err) => {
          console.error(err);
          setError("Upload failed. Please try again.");
          setUploading(false);
        },
        async () => {
          onUploadComplete({ farmId: farmId });
        }
      );

    } catch (err) {
      logError('Farm registration failed', err);
      setError(formatError(err));
      setUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <UploadCloud className="text-emerald-600" />
          Register New Farm
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Farm Name Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Farm Name</label>
          <input 
            type="text" 
            placeholder="e.g. Sunnybrook Estate"
            value={farmName}
            onChange={(e) => { setFarmName(e.target.value); setWarning(null); }}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
            required
          />
        </div>

        {/* File Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Boundary File (KMZ/KML)</label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
            <input 
              type="file" 
              accept=".kmz,.kml"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-emerald-700 font-medium">
                <FileText size={20} />
                {file.name}
              </div>
            ) : (
              <div className="text-slate-500">
                <p>Click to browse or drag file here</p>
                <p className="text-xs mt-1 text-slate-400">Max size 5MB</p>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {warning && (
          <div className="bg-amber-50 text-amber-800 p-3 rounded-md text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            {warning}
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            disabled={uploading || !file || !farmName}
            className={uploading ? "opacity-75 cursor-wait" : ""}
          >
            {uploading ? (
              <span className="flex items-center gap-2">Processing...</span>
            ) : (
              <span className="flex items-center gap-2"><CheckCircle size={18} /> Register Farm</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
